import type React from 'react'
import { useEffect, useRef, useState } from 'react'

import { useQuery } from '@tanstack/react-query'
import { ImageOffIcon } from 'lucide-react'

import { ImageViewer } from '@/components/atoms/ImageViewer'
import { cn } from '@/lib/utils'
import { firebaseStorageUrlQuery } from '@/state/firebase-storage/firebaseStorage'

const PORTRAIT_MAX_RATIO = 0.7
const LANDSCAPE_MIN_RATIO = 1.5
const WIDE_MIN_RATIO = 1.2

const MIN_FRAME_RATIO = 0.2
const MAX_FRAME_RATIO = 5

export interface MessageImageProps {
  readonly storagePath: string
  readonly width?: number
  readonly height?: number
  readonly dimmed?: boolean
  readonly queryScope?: string
}

const containerWidth = (width?: number, height?: number) => {
  if (!width || !height) {
    return '30%'
  }
  const aspectRatio = width / height
  if (aspectRatio < PORTRAIT_MAX_RATIO) {
    return '25%'
  }
  if (aspectRatio > LANDSCAPE_MIN_RATIO) {
    return '45%'
  }
  if (aspectRatio > WIDE_MIN_RATIO) {
    return '40%'
  }
  return '30%'
}

export const MessageImage: React.FC<MessageImageProps> = ({ storagePath, width, height, dimmed, queryScope }) => {
  const imageRef = useRef<HTMLImageElement>(null)
  const [viewerOpen, setViewerOpen] = useState(false)
  const [imageRect, setImageRect] = useState<DOMRect | null>(null)
  const [failedUrl, setFailedUrl] = useState<string | null>(null)
  const { data: imageUrl, isLoading, isError, refetch } = useQuery(firebaseStorageUrlQuery(storagePath, queryScope))
  const unavailable = isError || !imageUrl || failedUrl === imageUrl
  // Bounded so a forged pair of dimensions cannot stretch one card across the whole thread.
  const aspectRatio =
    width && height ? String(Math.min(Math.max(width / height, MIN_FRAME_RATIO), MAX_FRAME_RATIO)) : '16/9'

  // A failed image may carry a token the deletion trigger has just rotated: asking for the URL again
  // gets a fresh one, and gets the same one back when the file itself cannot be decoded.
  const handleImageError = () => {
    setFailedUrl(imageUrl ?? null)
    refetch().catch(() => null)
  }

  // The browser does not tell a file it cannot decode from bytes a dropped connection never
  // delivered, so a failed image is tried again whenever the window or the network comes back.
  useEffect(() => {
    if (!failedUrl) {
      return
    }
    const retry = () => {
      setFailedUrl(null)
      refetch().catch(() => null)
    }
    window.addEventListener('focus', retry)
    globalThis.addEventListener('online', retry)
    return () => {
      window.removeEventListener('focus', retry)
      globalThis.removeEventListener('online', retry)
    }
  }, [failedUrl, refetch])

  const handleClick = () => {
    if (imageUrl && imageRef.current) {
      setImageRect(imageRef.current.getBoundingClientRect())
      setViewerOpen(true)
    }
  }

  return (
    <>
      <div data-testid="message-image" style={{ width: containerWidth(width, height) }}>
        {isLoading ? (
          <div className="animate-pulse rounded-lg bg-gray-100" style={{ aspectRatio }} />
        ) : unavailable ? (
          <div className="flex items-center justify-center rounded-lg bg-gray-100" style={{ aspectRatio }}>
            <ImageOffIcon data-testid="message-image-error" className="h-8 w-8 text-gray-400" />
          </div>
        ) : (
          <img
            ref={imageRef}
            src={imageUrl}
            alt=""
            className={cn(
              'h-auto w-full cursor-pointer rounded-lg transition-opacity',
              dimmed ? 'opacity-50 hover:opacity-60' : 'hover:opacity-95'
            )}
            onClick={handleClick}
            onError={handleImageError}
          />
        )}
      </div>
      <ImageViewer imageUrl={imageUrl ?? null} open={viewerOpen} onOpenChange={setViewerOpen} initialRect={imageRect} />
    </>
  )
}
